import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { Col, Row } from 'reactstrap';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import {fetchAnalytics, fetchOverview, fetchRecentActivities} from '../../store/actions';
import AhoyTrack from '../../modules/AhoyTrack';
import DashboardBlock from '../../components/overview/OverviewBlock';
import MapBox from '../../components/MapBox/MapBox';
import TestDetailsDrawer from "../../components/drawers/TestDetailsDrawer";
import AiSelfServiceMertics from "../../components/overview/AISelfServiceMetrics/AiSelfServiceMertics";
import AppPerformanceIndicators from "../../components/overview/AppPerformanceIndicators/AppPerformanceIndicators";
import FeaturesIssuesDrawer from "../../components/drawers/FeaturedIssuesDrawer/FeaturesIssuesDrawer";
import { ReactComponent as ChevronsIcons } from "../../images/chevrons-left.svg";
import ComponentLoader from "../../components/ComponentLoader";

import { Monitor, PanelsLeftBottom, Globe } from 'lucide-react';
import { TimeRangeProvider } from '../../context/TimeRangeContext';
import AIBanner from '../../components/New/AIBanner';
import TitleSection from '../../components/New/TitleSection';
import HelpRequestsTile from '../../components/New/HelpRequestsTile';
import TimeRangeFilter from '../../components/New/TimeRangeFilter';
import DrillDownCard from '../../components/New/DrillDownCard';
import ApplicationStatus from '../../components/New/ApplicationStatus';
import UserIssuesTable from '../../components/New/UserIssuesTable';
import TopicList from '../../components/New/TopicList';
import { Card, CardContent } from "../../components/ui/card";

import styles from './overview.module.scss';

const Overview = () => {
  const { data: activitiesData, loading: isRecentActivitiesLoading } = useSelector((state) => state.RecentActivities);
  const overviewStats = useSelector((state) => state.DashboardAnalytics);
  const overviewLoading = overviewStats?.overview_loading;

  const [globalRange, setGlobalRange] = useState(null);
  const [testId, setTestId] = useState(null);

  const [isFeaturedIssuesDrawerOpen, setIsFeaturedIssuesDrawerOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const getOverviewRange = () => {
    switch (globalRange) {
      case '72h':
        return '72h';

      case '48h':
        return '48h';

      default:
        return '24h';
    }
  }

  useEffect(() => {
    AhoyTrack();
    dispatch(fetchRecentActivities(history));
    dispatch(fetchAnalytics('active_user_result'));
    dispatch(fetchOverview(getOverviewRange()));
  }, [globalRange]);

  const onMapMarkerClick = (id) => {
    setTestId(id);
    setIsDrawerOpen(true);
  };

  const onDrawerClose = () => {
    setTestId(null);
    setIsDrawerOpen(false);
  };

  return (
    <TimeRangeProvider>
      <div className="flex-1 p-2 md:p-6 overflow-auto">
        <AIBanner hoursSaved={overviewStats?.savedHours} isLoading={overviewLoading}/>

        <div className="flex flex-row justify-between items-center pb-4">
          <TitleSection
            title="Command Center"
            subtitle="Real-time overview of your tech environment"
          />
          <TimeRangeFilter setGlobalRange={setGlobalRange}/>
        </div>

        <HelpRequestsTile issuesNumber={overviewStats?.discussionStats?.escalatedDiscussions || 0}/>

        {/*
        <Row className="page-title align-items-center">
          <Col sm={12} className={styles.header}>
              <div className="page-title-box">
                <h2>Overview</h2>
              </div>
              <button
                className="featured-issue-btn"
                onClick={() => setIsFeaturedIssuesDrawerOpen(true)}
              >
                <ChevronsIcons />
                <span>Featured Issues</span>
              </button>
          </Col>
        </Row>
        */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <DrillDownCard
              icon={<Monitor size={24} className="text-indigo-500" />}
              title="Device Issues"
              count={overviewStats?.issuesByType?.device || 0}
              description="total issues"
              route="/admin/issues?tab=device"
              actionText="View Devices"
              isLoading={overviewLoading}
            />
            <DrillDownCard
              icon={<PanelsLeftBottom size={24} className="text-purple-500" />}
              title="Application Issues"
              count={overviewStats?.issuesByType?.application || 0}
              description="total issues"
              route="/admin/issues?tab=application"
              actionText="View Applications"
              isLoading={overviewLoading}
            />
            <DrillDownCard
              icon={<Globe size={24} className="text-blue-500" />}
              title="Network Issues"
              count={overviewStats?.issuesByType?.network || 0}
              description="total issues"
              route="/admin/issues?tab=network"
              actionText="View Network"
              isLoading={overviewLoading}
            />
        </div>

        <Card className="mb-4">
          <CardContent className="p-4">
            <ApplicationStatus applications={overviewStats?.appUptimes} isLoading={overviewLoading}/>
          </CardContent>
        </Card>

        <DashboardBlock>
          <div className="flex justify-between xl:gap-6 gap-4 mb-4 xl:flex-row flex-col">
            {/* isRecentActivitiesLoading && <ComponentLoader />} */}
            <div className="lg:col-span-3 bg-white p-5 rounded-lg shadow-sm w-full">
              <UserIssuesTable users={overviewStats?.issuesByUser} isLoading={overviewLoading}/>
            </div>
            {!isRecentActivitiesLoading &&
              <div className="lg:col-span-2 bg-white p-5 rounded-lg shadow-sm w-full">
                <MapBox
                  markers={activitiesData?.features || []}
                  onMapMarkerClick={onMapMarkerClick}
                />
              </div>
            }
          </div>
        </DashboardBlock>

        <TopicList trendingTopics={overviewStats?.trendingTopics} isLoading={overviewLoading}/>

        {/*
          <AppPerformanceIndicators />
        */}
      </div>

      <FeaturesIssuesDrawer
        isOpen={isFeaturedIssuesDrawerOpen}
        onHandlerClose={() => setIsFeaturedIssuesDrawerOpen(false)}
      />

      <TestDetailsDrawer
        isOpen={isDrawerOpen}
        onHandlerClose={onDrawerClose}
        testId={testId}
      />
    </TimeRangeProvider>
  );
};

export default Overview;
